import { useLocation } from '@tanstack/react-router'
import { SidebarTrigger } from '@deepguide-ai/ui/sidebar'
import {
  AlertTriangle,
  BarChart3,
  Activity,
  Blocks,
  BookOpen,
  Boxes,
  Building2,
  CheckSquare,
  ClipboardCheck,
  Database,
  FileArchive,
  FileCheck2,
  FileQuestion,
  FileText,
  Globe,
  Inbox,
  Layers,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  UserCheck,
  Users,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface MainHeaderProps {
  children?: React.ReactNode
  className?: string
  leading?: React.ReactNode
  showSidebarTrigger?: boolean
}

export default function MainHeader({
  children,
  className,
  leading,
  showSidebarTrigger,
}: MainHeaderProps) {
  const { pathname, searchStr } = useLocation()
  const CurrentRouteIcon = getMainHeaderRouteIcon(pathname, searchStr)
  const routeLeading =
    leading ??
    (CurrentRouteIcon ? (
      <CurrentRouteIcon className='text-text-body h-4 w-4 shrink-0' />
    ) : null)
  const shouldShowSidebarTrigger = showSidebarTrigger ?? !routeLeading

  return (
    <div
      className={cn(
        'border-border sticky top-0 z-10 flex !h-[50px] shrink-0 items-center gap-2 border-b bg-card pl-4 leading-[50px]',
        className
      )}
    >
      {shouldShowSidebarTrigger ? (
        <SidebarTrigger className='-ml-1' />
      ) : (
        routeLeading
      )}
      {children}
    </div>
  )
}

function getMainHeaderRouteIcon(pathname: string, searchStr: string) {
  if (searchStr.includes('from=tasks')) return CheckSquare

  if (pathname.includes('/anonymous-report')) return ShieldCheck
  if (pathname.includes('/employee-center/pending-policies')) return Shield
  if (pathname.includes('/employee-center/acknowledgments')) return ShieldCheck
  if (pathname.includes('/employee-center/training')) return BookOpen
  if (pathname.includes('/employee-center/devices')) return Database
  if (pathname.includes('/employee-center')) return Shield

  if (pathname.includes('/auditor-center')) {
    if (pathname.includes('/controls')) return ShieldCheck
    if (pathname.includes('/audit-evidence')) return FileArchive
    if (pathname.includes('/policies')) return FileText
    if (pathname.includes('/people')) return Users
    if (pathname.includes('/timeline')) return Activity
    if (pathname.includes('/dashboard')) return BarChart3
    if (pathname.includes('/risks')) return AlertTriangle
    return UserCheck
  }

  if (pathname.includes('/frameworks')) return Layers
  if (pathname.includes('/controls')) return Shield
  if (pathname.includes('/tests')) return Shield
  if (pathname.includes('/risks')) return AlertTriangle
  if (pathname.includes('/vendors')) return Building2
  if (pathname.includes('/assets')) return Boxes
  if (pathname.includes('/policies')) return ShieldCheck
  if (pathname.includes('/documents')) return FileCheck2
  if (pathname.includes('/runbooks')) return ClipboardCheck
  if (pathname.includes('/people')) return Users
  if (pathname.includes('/integrations')) return Blocks
  if (pathname.includes('/audits')) return UserCheck
  if (pathname.includes('/trust-center')) return Globe
  if (pathname.includes('/questionnaires')) return FileQuestion
  if (pathname.includes('/program')) return SlidersHorizontal
  if (pathname.includes('/settings')) return Settings
  if (pathname.includes('/evidence')) return Database
  if (pathname.includes('/dashboard')) return BarChart3
  if (pathname.includes('/tasks')) return CheckSquare
  if (pathname.includes('/inbox')) return Inbox

  return null
}
